import { TTemplateIconsCode } from 'components/TemplatesItem/TemplatesIcon';

export interface IResponseWrapper<T> {
  success: boolean;
  message: string;
  systemMessage: string;
  data: T;
  dataTotalCount: number;
}

export interface ICountry {
  id: number;
  name: string;
  codeIso3: string;
  adminLevel: number;
  globalId: string;
  parentId: number;
  whoRegionId: number;
  parent: string;
  whoRegion: {
    id: number;
    name: string;
  };
}

export interface IFile {
  name: string;
  fileName: string;
}

export interface ITemplateResource {
  name: string;
  description: string | null;
  link: string;
  position: number;
}

export interface ITemplate {
  id: number;
  category: ICategory;
  code: TTemplateIconsCode;
  details: {
    description: string;
    source: string;
  }[];
  name: string;
  position: number;
  isComingLater: boolean;
  source: string | null;
  note: string | null;
  resources: ITemplateResource[] | null;
  type: templateType;
}

export interface ITemplateCategories {
  [name: string]: ITemplate[];
}

export interface IType {
  id: number;
  name: string;
  code: TTemplateIconsCode;
  category: ICategory;
}

export interface IPlanAccess {
  download: boolean;
  edit: boolean;
}

export interface IUser {
  azureId: number;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  principalName: string;
  role: string;
  id?: number;
  roles: IUserRole[];
}

type PlanListItemUser = Pick<IUser, 'id' | 'firstName' | 'lastName' | 'azureId'>;
type PlanListItemCountry = Pick<ICountry, 'id' | 'name' | 'codeIso3' | 'adminLevel' | 'globalId'>;

export interface IPlan {
  typeId: number;
  countryId: number;
  adminAreaId: number | null;
  file: IFile;
  permission: string;
}

export interface IPlanListItem {
  id: number;
  createdDate: Date;
  updatedDate: Date;
  createdBy: PlanListItemUser;
  updatedBy: PlanListItemUser;
  file: IFile;
  type: IType;
  country: PlanListItemCountry;
  adminArea: PlanListItemCountry;
  access: IPlanAccess;
  permission: string | null;
}

export interface IPlanProgress {
  type: ITemplate;
  plan: IPlanListItem;
}

export interface IPlanResult {
  id: number;
  typeId: number;
  countryId: number;
  createdDate: Date;
  country: PlanListItemCountry;
  file: IFile;
  type: IType;
}

export interface IRolePermission {
  id: Permission | string;
  name: string;
  description: string;
}

export interface IRole {
  id: number;
  name: string;
  scope: RoleScope;
  type: RoleType | string;
  permissions: IRolePermission[];
}

export interface IRoleCountry {
  id: number;
  name: string;
  globalId: string;
}

export interface IUserRole {
  id: number;
  userId: number;
  roleId: number;
  countryId: number;
  regionId: number;

  role: IRole;
  country: IRoleCountry | null;
}

export interface PermissionScope {
  skip?: boolean;
  countryGlobalId?: string | null;
  entityCreatedByAzureId?: number | null;
}

export interface IPermissionOption {
  id: number;
  title: string;
  translationId: string;
  enable: boolean | null;
  permissions: {
    enable: SuggestionProps;
    disable: SuggestionProps;
  };
}

export interface IPermissionSuggestion {
  id: PlanPermission;
  externalId: unknown;
  title: string;
  translationId: string;
  hasMembers: boolean;
}

export interface ICategory {
  code: string;
  name: string;
}

export interface ICategory {
  code: string;
  name: string;
}

export type permissionId = number;
export type SuggestionProps = Record<PlanPermission, { enable: boolean } | undefined>;
export type templateType = 'template' | 'result';

export enum RoleScope {
  Global = 'global',
  Regional = 'regional',
  Country = 'country',
  User = 'user',
}

export enum RoleType {
  Nip = 'nip',
}

export enum Permission {
  GeneralAll = 'nip/general/all', // User has full rights.
  GeneralAccess = 'nip/general/access', // User can open the portal and view everything without editing and files downloading.
  PlanEdit = 'nip/plans/edit', // User can create, update, or delete a plan. User can download a template or a plan file.
  PlanTemplateDownload = 'nip/plans/template/download', // User can download an empty template on the Template page.
  PlanFileDownload = 'nip/plans/file/download', // User can download a filled plan file on the Uploads page.
}

export enum PlanPermission {
  All = 'all',
  WHOUsersRegionalCountryFp = 'who_users_regional_country_fp',
  RegionalCountryFp = 'regional_country_fp',
  CountryFp = 'country_fp',
}
